import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { BarChart3, CheckCircle2, XCircle, Loader2, Mail, Clock } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Module-level result cache keyed by token. The verify-email endpoint is a
// one-shot (the token is deleted on success). React 18 StrictMode + dev-time
// remounts can fire the effect twice, so we cache the result and let the
// second mount read from it instead of triggering a second 400 "already-used"
// call that would clobber the success state.
const _resultByToken = new Map();
const _waitersByToken = new Map();
const _notifyWaiters = (token, result) => {
  const waiters = _waitersByToken.get(token);
  if (waiters) { waiters.forEach((cb) => { try { cb(result); } catch { /* no-op */ } }); _waitersByToken.delete(token); }
};
const _subscribe = (token, cb) => {
  let set = _waitersByToken.get(token);
  if (!set) { set = new Set(); _waitersByToken.set(token, set); }
  set.add(cb);
  return () => set.delete(cb);
};

export default function VerifyEmailPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [status, setStatus] = useState('verifying'); // verifying | success | pending | error
  const [error, setError] = useState('');
  const [resendEmail, setResendEmail] = useState('');
  const [resendBusy, setResendBusy] = useState(false);
  const [resendDone, setResendDone] = useState(false);

  useEffect(() => {
    const token = searchParams.get('token');
    if (!token) {
      setStatus('error');
      setError('No verification token in the URL.');
      return;
    }

    const apply = (result) => {
      if (result?.ok) {
        if (result.pending_approval) {
          // Email verified but still awaiting super-admin approval — no session issued.
          setStatus('pending');
        } else {
          setStatus('success');
          setTimeout(() => { window.location.href = '/dashboard'; }, 1500);
        }
      } else {
        setStatus('error');
        setError(result?.error || 'Verification failed');
      }
    };

    const cached = _resultByToken.get(token);
    if (cached === 'pending') return _subscribe(token, apply);
    if (cached) { apply(cached); return; }

    _resultByToken.set(token, 'pending');
    (async () => {
      let result;
      try {
        const res = await fetch(`${BACKEND_URL}/api/auth/verify-email`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify({ token }),
        });
        const data = await res.json().catch(() => ({}));
        if (!res.ok) throw new Error(data.detail || data.message || 'This verification link is invalid or has expired.');
        result = { ok: true, pending_approval: !!data.pending_approval };
      } catch (err) {
        result = { ok: false, error: err.message || 'Verification failed' };
      }
      _resultByToken.set(token, result);
      _notifyWaiters(token, result);
      apply(result);
    })();
  }, [searchParams]);

  const handleResend = async (e) => {
    e.preventDefault();
    if (!resendEmail.trim()) return;
    setResendBusy(true);
    try {
      await fetch(`${BACKEND_URL}/api/auth/resend-verification`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: resendEmail }),
      });
      setResendDone(true);
    } finally {
      setResendBusy(false);
    }
  };

  return (
    <div className="min-h-screen w-full flex items-center justify-center px-4 py-12" style={{ background: 'radial-gradient(ellipse at top, rgba(59, 130, 246, 0.06) 0%, rgba(0, 0, 0, 0) 60%), #0a0a0b' }}>
      <div className="w-full max-w-md">
        <div className="flex items-center gap-3 mb-8 justify-center">
          <div className="p-2 rounded-xl bg-blue-500/10 border border-blue-500/20">
            <BarChart3 className="w-6 h-6 text-blue-400" strokeWidth={1.5} />
          </div>
          <h1 className="font-heading text-2xl font-bold text-white tracking-tight">INZARA</h1>
        </div>

        <div className="rounded-2xl p-8 text-center" style={{ background: 'rgba(255, 255, 255, 0.02)', backdropFilter: 'blur(20px)', border: '1px solid rgba(255, 255, 255, 0.06)' }}>
          {status === 'verifying' && (
            <div data-testid="verify-loading">
              <Loader2 className="w-12 h-12 text-blue-400 mx-auto mb-3 animate-spin" />
              <h2 className="text-xl font-heading font-semibold text-white mb-2">Verifying your email…</h2>
              <p className="text-sm text-zinc-400">Hang tight — this only takes a second.</p>
            </div>
          )}

          {status === 'success' && (
            <div data-testid="verify-success">
              <CheckCircle2 className="w-12 h-12 text-emerald-400 mx-auto mb-3" />
              <h2 className="text-xl font-heading font-semibold text-white mb-2">Email verified</h2>
              <p className="text-sm text-zinc-400 mb-6">Welcome to INZARA. Your 14-day trial has started — taking you to your dashboard…</p>
              <Loader2 className="w-5 h-5 text-zinc-500 mx-auto animate-spin" />
            </div>
          )}

          {status === 'pending' && (
            <div data-testid="verify-pending">
              <Clock className="w-12 h-12 text-amber-400 mx-auto mb-3" />
              <h2 className="text-xl font-heading font-semibold text-white mb-2">Email verified — awaiting approval</h2>
              <p className="text-sm text-zinc-400 mb-6 leading-relaxed">
                Thanks! Your email is confirmed. Our team is reviewing your access request — usually within 1 business day. You&apos;ll get another email the moment your trial goes live.
              </p>
              <Button onClick={() => navigate('/login')} variant="outline" className="rounded-xl bg-white/5 border-white/10 text-zinc-300" data-testid="verify-pending-back-to-login">
                Back to Sign In
              </Button>
            </div>
          )}

          {status === 'error' && (
            <div data-testid="verify-error">
              <XCircle className="w-12 h-12 text-red-400 mx-auto mb-3" />
              <h2 className="text-xl font-heading font-semibold text-white mb-2">Verification failed</h2>
              <p className="text-sm text-zinc-400 mb-6 leading-relaxed">{error}</p>

              {!resendDone ? (
                <form onSubmit={handleResend} className="space-y-3 text-left">
                  <p className="text-xs text-zinc-500">If your link expired, enter your email to receive a new one:</p>
                  <div className="space-y-1.5">
                    <Label className="text-zinc-300 text-xs">Email</Label>
                    <div className="relative">
                      <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-500" />
                      <Input
                        data-testid="verify-resend-email"
                        type="email"
                        placeholder="you@company.com"
                        value={resendEmail}
                        onChange={e => setResendEmail(e.target.value)}
                        className="pl-10 bg-white/5 border-white/10 text-white placeholder:text-zinc-600 focus:border-blue-500/50 rounded-xl"
                        required
                      />
                    </div>
                  </div>
                  <Button data-testid="verify-resend-btn" type="submit" disabled={resendBusy} className="w-full rounded-xl bg-blue-500 hover:bg-blue-600 text-white">
                    {resendBusy ? 'Sending…' : 'Send a new link'}
                  </Button>
                </form>
              ) : (
                <p className="text-sm text-emerald-300" data-testid="verify-resend-done">If an unverified account exists for that email, a new link is on its way.</p>
              )}

              <Button onClick={() => navigate('/login')} variant="outline" className="mt-6 rounded-xl bg-white/5 border-white/10 text-zinc-300" data-testid="verify-back-to-login">
                Back to Sign In
              </Button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
